/**
 * Case Key Service — Client-Side Envelope Encryption
 *
 * Handles case key generation, wrapping, and unwrapping using Web Crypto API.
 * This is a CLIENT-SIDE ONLY service — no Prisma, no server imports.
 * Server-side envelope CRUD is handled by caseKeyController.ts.
 *
 * Key hierarchy:
 *   User passphrase → KEK (PBKDF2) → encrypts RSA private key
 *   RSA public key → wraps per-case AES key (stored as CaseKeyEnvelope)
 *   AES case key → encrypts case data (encryptedPayload)
 *
 * Security design:
 *   - Case keys are CryptoKey objects, never exposed as raw bytes in JS memory
 *   - Uses wrapKey/unwrapKey API to avoid raw key material in JavaScript
 *   - Unwrapped keys are non-extractable (locked inside browser crypto subsystem)
 */

import { DecryptionError, EncryptionError } from './encryptionService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CASE_KEY_ALGORITHM = 'AES-GCM';
const CASE_KEY_LENGTH = 256; // bits
const ENVELOPE_ALGORITHM = 'RSA-OAEP';
const RSA_HASH = 'SHA-256';
const TAG_LENGTH = 128; // bits — explicit, matches encryptionService pattern
const IV_LENGTH = 12; // bytes (96 bits — recommended for GCM)
const MIN_CIPHERTEXT_LENGTH = IV_LENGTH + TAG_LENGTH / 8 + 1; // IV + tag + 1 byte min

/** Prefix for case-key-encrypted field values (parallel to ENC:/HENC: for RSA) */
export const CASEKEY_PREFIX = 'CASEKEY:';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WrappedCaseKey {
  encryptedCaseKey: string; // base64
  algorithm: string;
}

// ---------------------------------------------------------------------------
// PEM Key Import (dedicated for envelope wrapping/unwrapping)
// ---------------------------------------------------------------------------

/**
 * Import an RSA public key from PEM format for key wrapping.
 * Uses 'wrapKey' usage (not 'encrypt') for proper semantics.
 */
async function importWrappingKey(pem: string): Promise<CryptoKey> {
  try {
    const pemContents = pem
      .replace('-----BEGIN PUBLIC KEY-----', '')
      .replace('-----END PUBLIC KEY-----', '')
      .replace(/\s/g, '');
    const binaryDer = base64ToArrayBuffer(pemContents);

    return await window.crypto.subtle.importKey(
      'spki',
      binaryDer,
      { name: ENVELOPE_ALGORITHM, hash: RSA_HASH },
      false,
      ['wrapKey'],
    );
  } catch (error) {
    throw new EncryptionError(
      `Failed to import wrapping key: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'WRAPPING_KEY_IMPORT_FAILED',
    );
  }
}

/**
 * Import an RSA private key from PEM format for key unwrapping.
 * Uses 'unwrapKey' usage (not 'decrypt') for proper semantics.
 */
async function importUnwrappingKey(pem: string): Promise<CryptoKey> {
  try {
    const pemContents = pem
      .replace('-----BEGIN PRIVATE KEY-----', '')
      .replace('-----END PRIVATE KEY-----', '')
      .replace(/\s/g, '');
    const binaryDer = base64ToArrayBuffer(pemContents);

    return await window.crypto.subtle.importKey(
      'pkcs8',
      binaryDer,
      { name: ENVELOPE_ALGORITHM, hash: RSA_HASH },
      false, // non-extractable
      ['unwrapKey'],
    );
  } catch (error) {
    throw new EncryptionError(
      `Failed to import unwrapping key: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'UNWRAPPING_KEY_IMPORT_FAILED',
    );
  }
}

// ---------------------------------------------------------------------------
// Case Key Generation
// ---------------------------------------------------------------------------

/**
 * Generate a random AES-256-GCM case key.
 * Returns a CryptoKey object — raw bytes never exposed to JavaScript.
 * The key is extractable (required for wrapKey to work), but raw export
 * is handled internally by the browser crypto subsystem during wrapping.
 */
export async function generateCaseKey(): Promise<CryptoKey> {
  try {
    return await window.crypto.subtle.generateKey(
      { name: CASE_KEY_ALGORITHM, length: CASE_KEY_LENGTH },
      true, // extractable — required for wrapKey API
      ['encrypt', 'decrypt'],
    );
  } catch (error) {
    throw new EncryptionError(
      `Failed to generate case key: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'CASE_KEY_GENERATION_FAILED',
    );
  }
}

// ---------------------------------------------------------------------------
// Key Wrapping (RSA-OAEP via wrapKey/unwrapKey API)
// ---------------------------------------------------------------------------

/**
 * Wrap (encrypt) a case key with a user's RSA public key.
 * Uses crypto.subtle.wrapKey — raw key bytes never enter JavaScript memory.
 *
 * @param caseKey      - CryptoKey AES case key
 * @param publicKeyPem - User's RSA-4096 public key in PEM format
 * @returns Base64-encoded wrapped key
 */
export async function wrapCaseKey(caseKey: CryptoKey, publicKeyPem: string): Promise<string> {
  try {
    const wrappingKey = await importWrappingKey(publicKeyPem);

    const wrappedBytes = await window.crypto.subtle.wrapKey('raw', caseKey, wrappingKey, {
      name: ENVELOPE_ALGORITHM,
    });

    return arrayBufferToBase64(wrappedBytes);
  } catch (error) {
    if (error instanceof EncryptionError) throw error;
    throw new EncryptionError(
      `Failed to wrap case key: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'CASE_KEY_WRAP_FAILED',
    );
  }
}

/**
 * Unwrap (decrypt) a case key with a user's RSA private key.
 * Returns a non-extractable CryptoKey — raw bytes locked in browser crypto.
 *
 * @param wrappedKeyBase64 - Base64-encoded wrapped case key
 * @param privateKeyPem    - User's RSA-4096 private key in PEM format
 * @returns Non-extractable CryptoKey for AES-GCM encrypt/decrypt
 */
export async function unwrapCaseKey(
  wrappedKeyBase64: string,
  privateKeyPem: string,
): Promise<CryptoKey> {
  try {
    const unwrappingKey = await importUnwrappingKey(privateKeyPem);
    const wrappedBytes = base64ToArrayBuffer(wrappedKeyBase64);

    return await window.crypto.subtle.unwrapKey(
      'raw',
      wrappedBytes,
      unwrappingKey,
      { name: ENVELOPE_ALGORITHM },
      { name: CASE_KEY_ALGORITHM, length: CASE_KEY_LENGTH },
      false, // non-extractable — key material locked in browser crypto
      ['encrypt', 'decrypt'],
    );
  } catch (error) {
    if (error instanceof EncryptionError) throw error;
    throw new DecryptionError(
      `Failed to unwrap case key: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'CASE_KEY_UNWRAP_FAILED',
    );
  }
}

/**
 * Unwrap a case key as EXTRACTABLE — for re-wrapping with another user's public key.
 * Only use this when granting access to another participant.
 * The extractable key should not be cached or stored long-term.
 */
export async function unwrapCaseKeyExtractable(
  wrappedKeyBase64: string,
  privateKeyPem: string,
): Promise<CryptoKey> {
  try {
    const unwrappingKey = await importUnwrappingKey(privateKeyPem);
    const wrappedBytes = base64ToArrayBuffer(wrappedKeyBase64);

    return await window.crypto.subtle.unwrapKey(
      'raw',
      wrappedBytes,
      unwrappingKey,
      { name: ENVELOPE_ALGORITHM },
      { name: CASE_KEY_ALGORITHM, length: CASE_KEY_LENGTH },
      true, // extractable — required for wrapKey to re-wrap for another user
      ['encrypt', 'decrypt'],
    );
  } catch (error) {
    if (error instanceof EncryptionError) throw error;
    throw new DecryptionError(
      `Failed to unwrap case key (extractable): ${error instanceof Error ? error.message : 'Unknown error'}`,
      'CASE_KEY_UNWRAP_EXTRACTABLE_FAILED',
    );
  }
}

// ---------------------------------------------------------------------------
// Case Data Encryption / Decryption (using case key)
// ---------------------------------------------------------------------------

/**
 * Encrypt an object with an AES-256-GCM case key.
 * Returns CASEKEY: prefixed base64 string containing IV + ciphertext + tag.
 */
export async function encryptWithCaseKey(data: object, caseKey: CryptoKey): Promise<string> {
  try {
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const plaintext = new TextEncoder().encode(JSON.stringify(data));

    const ciphertext = await window.crypto.subtle.encrypt(
      { name: CASE_KEY_ALGORITHM, iv, tagLength: TAG_LENGTH },
      caseKey,
      plaintext,
    );

    // Pack: [12-byte IV] + [ciphertext+tag]
    const packed = new Uint8Array(iv.byteLength + ciphertext.byteLength);
    packed.set(iv, 0);
    packed.set(new Uint8Array(ciphertext), iv.byteLength);

    return CASEKEY_PREFIX + arrayBufferToBase64(packed.buffer);
  } catch (error) {
    if (error instanceof EncryptionError) throw error;
    throw new EncryptionError(
      `Failed to encrypt with case key: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'CASE_DATA_ENCRYPT_FAILED',
    );
  }
}

/**
 * Decrypt a CASEKEY:-prefixed (or raw base64) ciphertext with an AES-256-GCM case key.
 * Expects format: [12-byte IV] + [ciphertext+tag].
 * Filters dangerous keys (__proto__, constructor, prototype) from parsed result.
 */
export async function decryptWithCaseKey<T extends object = object>(
  ciphertextBase64: string,
  caseKey: CryptoKey,
): Promise<T> {
  try {
    // Strip CASEKEY: prefix if present
    const raw = ciphertextBase64.startsWith(CASEKEY_PREFIX)
      ? ciphertextBase64.slice(CASEKEY_PREFIX.length)
      : ciphertextBase64;
    const packed = base64ToArrayBuffer(raw);

    // Validate minimum length: IV (12) + tag (16) + at least 1 byte of ciphertext
    if (packed.byteLength < MIN_CIPHERTEXT_LENGTH) {
      throw new DecryptionError(
        'Ciphertext too short — must contain IV, authentication tag, and data',
        'CASE_DATA_INVALID_LENGTH',
      );
    }

    const iv = packed.slice(0, IV_LENGTH);
    const ciphertext = packed.slice(IV_LENGTH);

    const plaintext = await window.crypto.subtle.decrypt(
      { name: CASE_KEY_ALGORITHM, iv, tagLength: TAG_LENGTH },
      caseKey,
      ciphertext,
    );

    // Use a reviver to strip prototype pollution keys at all nesting levels
    const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
    const parsed = JSON.parse(new TextDecoder().decode(plaintext), (key, value) =>
      DANGEROUS_KEYS.has(key) ? undefined : value,
    );

    return parsed as T;
  } catch (error) {
    if (error instanceof DecryptionError) throw error;
    throw new DecryptionError(
      `Failed to decrypt with case key: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'CASE_DATA_DECRYPT_FAILED',
    );
  }
}

// ---------------------------------------------------------------------------
// Field-Level Case Key Encryption
// ---------------------------------------------------------------------------

/**
 * Encrypt a single field value with a case key.
 * Wraps the value as { __field: value } before encryption so it round-trips
 * through the same JSON envelope used by hybrid RSA encryption.
 * Returns a CASEKEY:-prefixed base64 string.
 */
export async function encryptFieldWithCaseKey(value: string, caseKey: CryptoKey): Promise<string> {
  return encryptWithCaseKey({ __field: value }, caseKey);
}

/**
 * Decrypt a single CASEKEY:-prefixed field value back to its plaintext string.
 * Expects the encrypted payload to contain { __field: string }.
 */
export async function decryptFieldWithCaseKey(
  ciphertext: string,
  caseKey: CryptoKey,
): Promise<string> {
  const result = await decryptWithCaseKey<{ __field: string }>(ciphertext, caseKey);
  return result.__field ?? '';
}

/**
 * Check if a value is encrypted with a case key (has CASEKEY: prefix).
 */
export function isCaseKeyEncrypted(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.startsWith(CASEKEY_PREFIX);
}

/**
 * Check if a value is encrypted with field-level RSA (has ENC: or HENC: prefix).
 */
export function isFieldLevelEncrypted(value: string | null | undefined): boolean {
  return typeof value === 'string' && (value.startsWith('ENC:') || value.startsWith('HENC:'));
}

/**
 * Check if a value is encrypted with any scheme (ENC:, HENC:, or CASEKEY:).
 */
export function isEncryptedValue(value: string | null | undefined): boolean {
  return isFieldLevelEncrypted(value) || isCaseKeyEncrypted(value);
}

/**
 * Check if a case's sensitive fields need re-encryption from RSA → envelope.
 * Returns true if any field has ENC:/HENC: prefix.
 */
export function needsReEncryption(caseData: Record<string, any>): boolean {
  const sensitiveFields = [
    'caseTitle',
    'plaintiff',
    'attorneyName',
    'attorneyEmail',
    'attorneyPhone',
    'attorneyBarNumber',
    'attorneyState',
    'firmName',
    'description',
    'documentUrl',
  ];
  return sensitiveFields.some((field) => isFieldLevelEncrypted(caseData[field]));
}

/**
 * Check if an intake request's sensitive fields need re-encryption from RSA → envelope.
 * Returns true if any field has ENC:/HENC: prefix.
 */
export function needsReEncryptionIntakeRequest(data: Record<string, any>): boolean {
  const sensitiveFields = [
    'plaintiff',
    'caseTitle',
    'description',
    'notes',
    'attorneyName',
    'attorneyEmail',
    'attorneyPhone',
    'firmName',
    'documentUrl',
    'appointmentType',
    'preferredDate',
    'patientPhone',
    'patientEmail',
    'patientDateOfBirth',
    'specialInstructions',
  ];
  return sensitiveFields.some((field) => isFieldLevelEncrypted(data[field]));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
